import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Grid, Row, Col } from '../../ui/Grid';
import { Tabs, Tab } from '../../ui/Tabs';

import Alert from '../../ui/Alert';
import ProgressBar from '../../ui/ProgressBar';
import { saveText } from '../../../core/save';
import {
  fetchDeepSEAJobStatus,
  fetchDeepSEAJobSubmittedVariants,
  fetchDeepSEAScoreList,
  fetchDeepSEAScores,
  resetDeepSEAState,
} from '../../../actions/DeepSEAActions';
import styles from "./DeepSEABrowserPage.css"; // eslint-disable-line
import VariantFeatureBrowser from '../../genome/VariantFeatureBrowser';
import VariantScatterPlot from '../../genome/VariantScatterPlot';
import VariantScatterTable from '../../genome/VariantScatterTable';
import JobStatusTable from './JobStatusTable';
import { getHttps, isExPecto, useInterval } from '../../../core/util';
import { backoffJobPollDelay } from '../../../core/jobs';
import { ANALYSES_META_INFO, DEEPSEA_ANALYSES } from '../../../settings';

const TRACK_STYLE = {
  type: 'default',
  style: {
    glyph: 'HISTOGRAM',
    BGCOLOR: '#105CC9',
    HEIGHT: 50,
    MIN: 0,
    MAX: 3,
  },
};

const EXPECTO_TRACK_STYLE = {
  type: 'default',
  style: {
    glyph: 'HISTOGRAM',
    BGCOLOR: '#105CC9',
    HEIGHT: 50,
    MIN: -0.5,
    MAX: 0.5,
  },
};

const DeepSEABrowserPage = ({ jobName }) => {
  const dispatch = useDispatch();
  const variantScores = useSelector(state => state.deepsea.variantScores);
  const variantScoresFailed = useSelector(
    state => state.deepsea.variantScoresFailed,
  );
  const scoreList = useSelector(state => state.deepsea.scoreList);
  const job = useSelector(state => state.deepsea.selectedJob);
  const [data, setData] = useState(null);
  const [delay, setDelay] = useState(1000);
  const [analysisShortName, setAnalysisShortName] = useState(
    DEEPSEA_ANALYSES.deepsea,
  );
  const [featureScoreTypeNames, setFeatureScoreTypeNames] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [submittedVariants, setSubmittedVariants] = useState([]);
  const [variantScoreTracks, setVariantScoreTracks] = useState([]);
  const [variantScoreTypeOptions, setVariantScoreTypeOptions] = useState([]);
  const [variantScoreTypes, setVariantScoreTypes] = useState([]);

  useEffect(() => {
    if (
      variantScoreTypes.length === 0 ||
      !variantScores ||
      Object.keys(variantScores).length === 0
    )
      setData(null);
    else {
      // Get the job sequence data from a score in order to combine each one with its related variant scores.
      // The resulting object is a valid repository for all variant scores regardless of source
      // (ie. client created or server)
      // TODO refactor so that we are getting `sequence` from a more legitimate source
      let variantData = Object.values(variantScores)[0].scores.map(
        (scoreWithSeq, idx) => {
          const { chromosome, ...sequence } = scoreWithSeq.sequence;
          const seq = Object.assign(sequence, { idx });
          seq.chromosome = chromosome;
          return seq;
        },
      );

      variantScoreTypes.forEach(scoreType => {
        variantData = variantData.reduce((result, seq, idx) => {
          const seqWithNewScoreValue = Object.assign({}, seq);
          if (
            variantScores &&
            variantScores[scoreType.short_name] &&
            variantScores[scoreType.short_name].scores[idx] !== undefined
          ) {
            seqWithNewScoreValue[scoreType.short_name] = parseFloat(
              variantScores[scoreType.short_name].scores[idx].value.toFixed(2),
            );
          } else {
            seqWithNewScoreValue[scoreType.short_name] = null;
          }
          result.push(seqWithNewScoreValue);
          return result;
        }, []);
      });

      if (job.expecto) {
        const remainIndices = variantData.map(remainingVariant =>
          submittedVariants.findIndex(
            subV =>
              subV.match(new RegExp(`\t${remainingVariant.position}\t`, 'g')) &&
              (subV.match(
                new RegExp(`^${remainingVariant.chromosome}\t`, 'g'),
              ) ||
                subV.match(
                  new RegExp(
                    `^${remainingVariant.chromosome.replace(/^chr/, '')}\t`,
                    'g',
                  ),
                )) &&
              subV.match(
                new RegExp(`\t${remainingVariant.ref_allele}\t`, 'g'),
              ) &&
              subV.match(
                new RegExp(
                  `\t${remainingVariant.alt_allele}([\\t\\n\\r]|$)`,
                  'g',
                ),
              ),
          ),
        );

        const missingVariants = submittedVariants.filter(
          (_, idx) => !remainIndices.includes(idx),
        );
        setFilteredVariants(missingVariants);
      }

      setData(variantData);
    }
  }, [variantScores, variantScoreTypes]);

  useEffect(() => {
    setVariantScoreTracks(
      variantScoreTypes
        .map(score => {
          let theStyle = TRACK_STYLE;
          if (isExPecto(score)) {
            theStyle = EXPECTO_TRACK_STYLE;
          }

          return {
            name: score.name,
            bwgURI:
              job.files[`${score.level.toLowerCase()}_${score.short_name}`]
                ?.url,
            pinned: true,
            style: [theStyle],
          };
        })
        .filter(track => track.bwgURI),
    );
    if (job.expecto) {
      dispatch(fetchDeepSEAJobSubmittedVariants()).then(variants => {
        if (variants === undefined) return;
        const lines = variants.trim().split('\n');
        if (lines[0][0] === '#') lines.shift(); // remove header line
        setSubmittedVariants(lines);
      });
    }

    setAnalysisShortName(
      job.insilico
        ? DEEPSEA_ANALYSES.ism
        : job.expecto
        ? DEEPSEA_ANALYSES.expecto
        : DEEPSEA_ANALYSES.deepsea,
    );
  }, [variantScoreTypes, job]);

  useEffect(() => {
    const tmpVariantScoreTypes = scoreList
      .filter(score => {
        if (job.expecto) {
          return isExPecto(score);
        }
        return true;
      })
      .filter(score => score.level === 'VARIANT' || score.level === 'SEQUENCE');
    setVariantScoreTypes(tmpVariantScoreTypes);
    setVariantScoreTypeOptions(
      tmpVariantScoreTypes.map(score => ({
        ...score,
        label: score.name,
        value: score.short_name,
      })),
    );

    setFeatureScoreTypeNames(
      scoreList
        .filter(score => score.level === 'FEATURE')
        .map(score => ({
          ...score,
          name: score.short_name,
          label: score.name,
        })),
    );
  }, [scoreList]);

  useEffect(() => {
    setDelay(1000);
    return () => dispatch(resetDeepSEAState()); // cleanup on unmount
  }, [dispatch, jobName]);

  useInterval(() => {
    dispatch(fetchDeepSEAJobStatus(jobName)).then(isSuccessful => {
      // Retry transient failures without requesting scores from stale status.
      if (!isSuccessful) {
        setDelay(backoffJobPollDelay);
        return;
      }
      // Terminal errors must not restart periodic checks or fetch score types.
      if (job?.status?.startsWith('error')) {
        setDelay(null);
        return;
      }
      const jobInProgress =
        job && (job.status === 'running' || job.status === 'queued');
      setDelay(jobInProgress ? 1000 : null);
      if (!jobInProgress) dispatch(fetchDeepSEAScoreList(jobName));
    });
  }, delay);

  useEffect(() => {
    if (variantScoreTypes.length)
      dispatch(fetchDeepSEAScores(variantScoreTypes, jobName));
  }, [variantScoreTypes]);

  const status = job && <JobStatusTable job={job} statusOnly />;

  let progressBar = null;
  if (!job || job.status === 'completed') {
    progressBar = (
      <ProgressBar
        label={`Loading ${ANALYSES_META_INFO[analysisShortName].name} results`}
      />
    );
  } else if (job && job.status === 'running') {
    progressBar = (
      <ProgressBar
        label={`${ANALYSES_META_INFO[analysisShortName].name} job is currently running...`}
      />
    );
  }

  const onFeatureInfo = (feature, info) => {
    const scoreName = info.tier.dasSource.name;
    const scoreShortName = variantScoreTypes.find(
      variantScore => variantScore.name === scoreName,
    ).short_name;
    const selectedPredictions = data.filter(
      variant =>
        variant.chromosome === feature.segment &&
        variant.position === feature.min,
    );
    selectedPredictions.sort((a, b) =>
      a[scoreShortName] < b[scoreShortName] ? 1 : -1,
    );
    info.setTitle(info.tier.dasSource.name);
    info.add('Ref Allele', selectedPredictions[0].ref_allele);
    info.add('Alt Alelle', selectedPredictions[0].alt_allele);
  };

  const progress = (
    <div className={styles.container}>
      {progressBar}
      <div style={{ width: '100%', margin: 'auto' }}>{status}</div>
    </div>
  );

  let title = 'Variant effect predictions';
  if (variantScoreTypes.length && variantScoreTypes[0].level === 'SEQUENCE')
    title = 'Chromatin feature effect predictions';
  const infoPanel = job && (
    <div>
      {job.title && <strong>{job.title}</strong>}
      {job.title && job.email && ' / '}
      {job.email && ` ${job.email}`}
      {job.title || job.email ? <br /> : null}
      {`created: ${new Date(job.created).toLocaleDateString()} / model: ${
        job.ds_model
      }`}
      <br />
      {job.files.gz_file && (
        <a href={getHttps(job.files.gz_file.url)}>Download results</a>
      )}
    </div>
  );

  // A failed score load leaves nothing to plot, but the header still tells the
  // user which job this is. The global banner says why the results are missing;
  // a progress bar would claim they are still coming.
  if (variantScoresFailed)
    return (
      <Grid fluid={false}>
        <Row>
          <Col md={12}>
            <div className={styles.container}>
              <div style={{ width: '100%', margin: 'auto' }}>{status}</div>
            </div>
          </Col>
        </Row>
      </Grid>
    );

  return (
    <Grid fluid={false}>
      <Row>
        <Col md={12}>
          {data && variantScoreTypeOptions ? (
            <div className={styles.container}>
              <div style={{ float: 'left', textAlign: 'right' }}>
                <h3>{title}</h3>
              </div>
              <div style={{ float: 'right', textAlign: 'right' }}>
                {infoPanel}
              </div>
              <div style={{ marginTop: 15, paddingBottom: 20 }}>
                <Tabs
                  defaultActiveKey={1}
                  className="tab-under"
                  id="deepsea-tabs"
                  bsStyle="pills"
                >
                  <Tab eventKey={1} title="Scatter Plot">
                    <VariantScatterPlot
                      data={data}
                      scoreOptions={variantScoreTypeOptions}
                    />
                  </Tab>
                  <Tab eventKey={2} title="Table">
                    <VariantScatterTable
                      data={data}
                      scoreList={variantScoreTypeOptions}
                    />
                  </Tab>
                </Tabs>
                {filteredVariants.length && job && job.expecto ? (
                  <Alert bsStyle="warning" style={{ marginTop: '15px' }}>
                    <i className="fa fa-exclamation-triangle" />
                    {` ${filteredVariants.length} of ${submittedVariants.length} submitted variants were
                    filtered out because they were further than 20 kilobases from the nearest TSS.  The filtered
                    variants are listed `}
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        saveText(
                          filteredVariants.join('\n'),
                          `${jobName}-filtered_variants.tsv`,
                        );
                      }}
                    >
                      <span
                        style={{
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                        }}
                      >
                        here
                      </span>
                    </button>
                    .
                  </Alert>
                ) : null}
              </div>
              <div>
                <h4>{`Genome browser (${job.genome_assembly})`}</h4>
                <VariantFeatureBrowser
                  models={featureScoreTypeNames}
                  sources={variantScoreTracks}
                  onFeatureInfo={onFeatureInfo}
                  variantData={data}
                  genome={job.genome_assembly}
                />
              </div>
            </div>
          ) : (
            progress
          )}
        </Col>
      </Row>
    </Grid>
  );
};

DeepSEABrowserPage.propTypes = {
  jobName: PropTypes.string.isRequired,
};

export default withStyles(styles)(DeepSEABrowserPage);
